import React, { FC, useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import type { Locale } from '@web-config-app/core';
import { useTranslate } from '@web-config-app/core-react';
import { StackLayout, UtilityText } from '@leagueplatform/genesis-core';
import { SelectMenuItemGroup } from '../../types/components.types';
import { SelectActionMenu } from '../select-action-menu/select-action-menu.component';
import { SelectActionMenuButton } from '../select-action-menu/select-action-menu-button';

/**
 * Properties for the LocaleSelect component.
 */
interface LocaleSelectProps {
  availableLocales: Locale[];
  onSelectLocale: (localeId: string) => void;
  disabled?: boolean;
  currentLocale: string;
}

/**
 * LocaleSelect component displays a dropdown menu to select a locale.
 * The currently active locale will render as the SelectActionMenu trigger label.
 *
 * The component UI displays a list of available locales that can be selected to
 * switch the current locale context. The selected locale is visually highlighted.
 *
 */
export const LocaleSelect: FC<LocaleSelectProps> = ({
  availableLocales,
  onSelectLocale,
  disabled,
  currentLocale,
}) => {
  const { formatMessage } = useIntl();

  const translate = useTranslate();

  const activeLocale = availableLocales.find(
    (locale) => locale.code === currentLocale,
  ) || {
    code: currentLocale,
    label: { text: currentLocale },
  };

  const localeName =
    activeLocale.label.text ??
    translate(activeLocale.label.key, activeLocale.label.values);

  const menuLabel = formatMessage(
    {
      id: 'LOCALE_SELECT',
      defaultMessage: 'Language select: {localeName} selected',
    },
    {
      localeName,
    },
  );

  const menuGroups = useMemo(() => {
    const groups: SelectMenuItemGroup[] = [
      {
        menuItems: availableLocales.map((locale) => ({
          id: locale.code,
          label: locale.isDefault
            ? `${locale.code} (${formatMessage({ id: 'DEFAULT' })})`
            : locale.code,
          action: () => onSelectLocale(locale.code),
          selected: locale.code === currentLocale,
        })),
      },
    ];

    return groups;
  }, [availableLocales, onSelectLocale, currentLocale, formatMessage]);

  return (
    <StackLayout
      horizontalAlignment="end"
      css={{ marginBlockEnd: '$oneAndHalf' }}
    >
      <UtilityText
        emphasis="subtle"
        css={{
          typography: '$overline',
          marginBlockEnd: '$quarter',
        }}
        // we hide this text from screen readers because the SelectActionMenu already provides a more descriptive accessible label
        aria-hidden="true"
      >
        {formatMessage({ id: 'LOCALE' })}
      </UtilityText>
      <SelectActionMenu menuGroups={menuGroups}>
        <SelectActionMenuButton
          triggerLabel={
            activeLocale.isDefault
              ? `${activeLocale.code} (${formatMessage({ id: 'DEFAULT' })})`
              : activeLocale.code
          }
          disabled={disabled}
          menuLabel={menuLabel}
        />
      </SelectActionMenu>
    </StackLayout>
  );
};
